import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// Import directly from app directory
import App from './app/App';
import { reportError, safeSerializeError } from './app/services/error-handling';

// Add global error handler to catch and log all errors
window.addEventListener('error', (event) => {
  // Use the enhanced error reporting
  reportError(event.error || event.message, 'GlobalError', {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    type: 'uncaught-exception'
  });
  
  // Prevent default to avoid webpack-dev-server overlay showing raw object
  if (process.env.NODE_ENV === 'development' && typeof event.error === 'object') {
    event.preventDefault();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  // Use safe serialization for better error messages
  const errorMessage = safeSerializeError(event.reason);
  
  // Determine if this is an expected/handled error type
  const isValidationError = errorMessage.includes('Invalid query parameters') ||
                            errorMessage.includes('must be a valid MongoDB ObjectId') ||
                            errorMessage.includes('validation failed');
  
  const isNetworkError = event.reason?.code === 'NETWORK_ERROR' || 
                         event.reason?.isNetworkError === true ||
                         errorMessage.includes('Unable to connect to the server') ||
                         errorMessage.includes('Network Error');
  
  const isExpectedError = isNetworkError ||
                          isValidationError ||
                          errorMessage.includes('WebSocket') ||
                          errorMessage.includes('401') ||
                          errorMessage.includes('403');
  
  // Use enhanced error reporting for unexpected errors
  if (!isExpectedError) {
    reportError(event.reason, 'UnhandledRejection', {
      type: 'unhandled-promise-rejection',
      promise: 'Promise object (not serialized)'
    });
  } else if (isValidationError) {
    // For validation errors, log a warning with the specific validation issue
    console.warn('Validation error detected:', errorMessage);
  } else if (isNetworkError) {
    // For network errors, log a simpler warning
    console.warn('Network connectivity issue detected. The application will retry automatically.');
  }
  
  // Always prevent default in development to avoid webpack overlay
  if (process.env.NODE_ENV === 'development') {
    event.preventDefault();
  }
});

const container = document.getElementById('root') as HTMLElement;
const app = (
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
);

// Use hydrateRoot when react-snap has pre-rendered the page, createRoot otherwise
if (container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, app);
} else {
  ReactDOM.createRoot(container).render(app);
}
